<template>
  <div class="w-100 mt-4 mb-4">
    <div class="d-flex flex-column align-items-center mb-5">
      <div class="d-flex align-items-center">
        <h2 class="mb-0">{{ title }}</h2>
        <button
          data-test="settings-button"
          class="btn btn-icon ms-2"
          :class="{
            'btn-secondary': activeSection === 'settings',
            'btn-phoenix-secondary': activeSection !== 'settings',
          }"
          :title="$t('settings.title')"
          @click="$emit('select:section', 'settings')"
        >
          <FontAwesomeIcon :icon="['fas', 'gear']" />
        </button>
        <label
          v-if="isAdmin"
          class="alert alert-subtle-warning text-center admin-extra-info ms-2 mb-0"
        >
          {{ company.id }}
        </label>
      </div>
    </div>

    <div class="row g-3 mb-4 justify-content-center">
      <!-- Only while Soldera owes the company money, e.g. after it sold GOs through Soldera.
           A fee-only or net-negative balance is settled against sales, not invoiced. -->
      <DashboardStatBlockComponent
        v-if="pendingPayouts.length > 0"
        class="col-6 border-translucent border-end"
        :title="$t('dashboard.companyData.money_pending')"
      >
        <template #value>
          <PendingPayoutFigure
            data-test="money-pending"
            class="mb-2"
            :parts="pendingPayouts"
            :isLoading
          />
        </template>
        <button
          class="btn"
          :class="{
            'btn-secondary': isSettingsTabOpen('submit-invoice'),
            'btn-subtle-primary': !isSettingsTabOpen('submit-invoice'),
          }"
          @click="$emit('open:settings-tab', 'submit-invoice')"
        >
          <FontAwesomeIcon icon="file-invoice-dollar" class="me-1" />
          {{ $t("dashboard.companyData.submit_invoice") }}
        </button>
        <button
          data-test="invoice-history-button"
          class="btn btn-icon ms-2"
          :class="{
            'btn-secondary': isSettingsTabOpen('invoice-history'),
            'btn-phoenix-secondary': !isSettingsTabOpen('invoice-history'),
          }"
          :title="$t('dashboard.companyData.invoice_history')"
          @click="$emit('open:settings-tab', 'invoice-history')"
        >
          <FontAwesomeIcon icon="folder-open" />
        </button>
      </DashboardStatBlockComponent>

      <DashboardStatBlockComponent
        class="col-6"
        :value="formatNumberWithSpaces(availableGos)"
        :unit="$t('common.mwh')"
        :title="$t('dashboard.companyData.available_guarantees_of_origin')"
        :isLoading
      >
        <button
          class="btn"
          :class="{
            'btn-secondary': activeSection === 'eacs',
            'btn-phoenix-secondary': activeSection !== 'eacs',
          }"
          @click="$emit('select:section', 'eacs')"
        >
          <FontAwesomeIcon
            :icon="activeSection === 'eacs' ? 'caret-down' : 'warehouse'"
            class="me-1"
          />
          {{ inventoryLabel }}
        </button>
      </DashboardStatBlockComponent>
    </div>
  </div>
</template>

<script setup lang="ts">
import { formatNumberWithSpaces } from "@/composables/formatUtils";
import type { PendingPayoutPart } from "@/composables/money/invoiceUtils";
import DashboardStatBlockComponent from "@/dashboard/common/DashboardStatBlockComponent.vue";
import PendingPayoutFigure from "@/money/PendingPayoutFigure.vue";
import type { Company } from "@/types";
import { computed } from "vue";

/** The dashboard's top-level sections: the EAC inventory, or settings with its tabs. */
export type EacDashboardSection = "eacs" | "settings";

/** The settings tabs the header links to directly. */
type LinkedSettingsTab = "submit-invoice" | "invoice-history";

/**
 * Header for the dashboards of companies that hold EACs rather than produce them (trader and
 * corporate views), laid out like the producer DashboardNavigationComponent.
 */
const props = defineProps<{
  company: Company;
  isAdmin?: boolean;
  activeSection: EacDashboardSection;
  activeSettingsTab: string;
  inventoryLabel: string;
  availableGos: number;
  /** What is owed per currency, as `usePendingInvoiceItems` decides it. */
  pendingPayouts: PendingPayoutPart[];
  isLoading?: boolean;
}>();

defineEmits<{
  "select:section": [section: EacDashboardSection];
  "open:settings-tab": [tabId: LinkedSettingsTab];
}>();

const title = computed(() =>
  props.company.registry_code
    ? `${props.company.name} (${props.company.registry_code})`
    : props.company.name
);

const isSettingsTabOpen = (tabId: LinkedSettingsTab) =>
  props.activeSection === "settings" && props.activeSettingsTab === tabId;
</script>

<style scoped lang="scss">
.admin-extra-info {
  padding: 0;
}
</style>
